import React from 'react';
import { Plus, Trash2, Package } from 'lucide-react';
import { fixEncodingIssues } from '../../utils/textUtils';

interface PropostaItemsProps {
  items: any[];
  setItems: (items: any[]) => void;
  servicos: any[];
}

export function PropostaItems({ items, setItems, servicos }: PropostaItemsProps) {
  const handleAddItem = () => {
    setItems([...items, { id: crypto.randomUUID(), tipo_servico_id: '', servicos: '', quantidade: 1, valor_unitario: 0, valor_total: 0 }]);
  };

  const updateItem = (idx: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[idx][field] = value;
    
    if (field === 'tipo_servico_id') {
      const selectedService = servicos.find(s => s.id.toString() === value.toString());
      if (selectedService && (!newItems[idx].servicos || newItems[idx].servicos.trim() === '')) {
        newItems[idx].servicos = fixEncodingIssues(selectedService.tipo);
      }
    }

    if (field === 'quantidade' || field === 'valor_unitario') {
      const qtd = parseFloat(newItems[idx].quantidade) || 0;
      const unit = parseFloat(newItems[idx].valor_unitario) || 0;
      newItems[idx].valor_total = qtd * unit;
    }
    setItems(newItems);
  };

  const removeItem = (idx: number) => {
    setItems(items.filter((_: any, i: number) => i !== idx));
  };

  const totalGeral = items.reduce((acc: number, item: any) => acc + (item.valor_total || 0), 0);

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-slate-200">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Package className="w-5 h-5 text-red-600 shrink-0" />
          Serviços e Valores
        </h3>
        <button
          type="button"
          onClick={handleAddItem}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors shadow-sm cursor-pointer"
        >
          <Plus size={16} strokeWidth={2.5} /> Adicionar Serviço
        </button>
      </div>

      {/* Visualização Desktop (Tabela) */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-left border-collapse min-w-[600px]">
          <thead className="bg-slate-50">
            <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <th className="p-3 w-1/4">Tipo de Serviço</th>
              <th className="p-3 w-1/4">Descrição do Serviço</th>
              <th className="p-3 w-1/6">Quantidade</th>
              <th className="p-3 w-1/6">Valor Unit. (R$)</th>
              <th className="p-3 w-1/6 text-right">Total (R$)</th>
              <th className="p-3 w-12 text-center">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {items.map((item: any, idx: number) => (
              <tr key={item.id || idx} className="group hover:bg-slate-50/50 transition-colors">
                <td className="p-3">
                  <select
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs"
                    value={item.tipo_servico_id}
                    onChange={e => updateItem(idx, 'tipo_servico_id', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    {servicos.map((s: any) => <option key={s.id} value={s.id}>{s.tipo}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <input
                    type="text"
                    placeholder="Ex: Diária de Ajudante"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs"
                    value={item.servicos || ''}
                    onChange={e => updateItem(idx, 'servicos', e.target.value)}
                  />
                </td>
                <td className="p-3">
                  <input
                    type="number"
                    min="1"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs"
                    value={item.quantidade}
                    onChange={e => updateItem(idx, 'quantidade', e.target.value)}
                  />
                </td>
                <td className="p-3">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs"
                    value={item.valor_unitario}
                    onChange={e => updateItem(idx, 'valor_unitario', e.target.value)}
                  />
                </td>
                <td className="p-3 text-right font-medium text-slate-700 text-xs">
                  {item.valor_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </td>
                <td className="p-3 text-center">
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors mx-auto block cursor-pointer"
                    title="Remover item"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-500 text-xs bg-slate-50/50">
                  Nenhum serviço adicionado. Clique em "Adicionar Serviço" para começar a montar a proposta.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200">
            <tr>
              <td colSpan={4} className="p-4 text-right font-semibold text-slate-600 uppercase text-xs tracking-wider">
                Total Geral da Proposta:
              </td>
              <td className="p-4 text-right">
                <div className="inline-block bg-slate-800 text-white font-bold text-base px-4 py-2 rounded-lg shadow-sm">
                  {totalGeral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </div>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Visualização Mobile (Cards Editáveis Responsivos) */}
      <div className="block md:hidden space-y-3">
        {items.map((item: any, idx: number) => (
          <div key={item.id || idx} className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-200">
              <span className="text-xs font-bold text-slate-700">Item #{idx + 1}</span>
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="text-xs text-rose-600 hover:text-rose-800 flex items-center gap-1 font-semibold p-1"
              >
                <Trash2 size={14} /> Remover
              </button>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Tipo de Serviço</label>
              <select
                className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 text-xs font-medium"
                value={item.tipo_servico_id}
                onChange={e => updateItem(idx, 'tipo_servico_id', e.target.value)}
              >
                <option value="">Selecione...</option>
                {servicos.map((s: any) => <option key={s.id} value={s.id}>{s.tipo}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Descrição do Serviço</label>
              <input
                type="text"
                placeholder="Ex: Diária de Ajudante"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 text-xs"
                value={item.servicos || ''}
                onChange={e => updateItem(idx, 'servicos', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Quantidade</label>
                <input
                  type="number"
                  min="1"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 text-xs"
                  value={item.quantidade}
                  onChange={e => updateItem(idx, 'quantidade', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Valor Unit. (R$)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 text-xs"
                  value={item.valor_unitario}
                  onChange={e => updateItem(idx, 'valor_unitario', e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-xs">
              <span className="text-slate-500 font-medium">Subtotal:</span>
              <strong className="text-red-600 font-bold">
                {item.valor_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </strong>
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <div className="p-6 text-center text-slate-500 text-xs bg-slate-50 rounded-xl border border-dashed border-slate-300">
            Nenhum serviço adicionado. Toque em "Adicionar Serviço" para começar.
          </div>
        )}

        <div className="bg-slate-900 text-white p-3.5 rounded-xl flex items-center justify-between shadow-sm">
          <span className="text-xs text-slate-300 uppercase tracking-wider font-semibold">Total Geral:</span>
          <span className="text-base font-extrabold text-red-400">
            {totalGeral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </span>
        </div>
      </div>
    </div>
  );
}
